import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import propTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import PageLoader from '../../../../../PageLoader2/PageLoader2';
import {
  useSheetFormStyles,
  useResourcesFormStyles,
  useSheetDetailsStyles,
} from '../../../../hooks';
import DatePickerInput from '../../../../../DatePickerInput';
import { getProjectName, checkUserRole, hasPermissionsFor } from 'helpers/_helpers';
import AlertModal from '../../../../../AlertModal/AlertModal';
import { isIOS, Option } from '../../../../../Option';
import OverlappingModal from '../../../../../OverlapsingModal';
import { updateSheetAddResource } from 'store/schedule/schedulePageOperation';
import { parse, startOfDay, format, endOfWeek, startOfWeek, subDays, addDays } from 'date-fns';
import { patchWorklogGeneralInfo, postWorklogCancelAndMove } from 'store/currentWorklog/operations';
import LocationCreator from './LocationCreator/LocationCreator';
import { Divider } from '@material-ui/core';
import _ from 'lodash';
import classnames from 'classnames';
import { AlternateContacts } from './components/AlternateContacts/AlternateContacts';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { globalBEDateFormat } from 'common/dateFormatConfig';
import { useWeekStart } from 'hooks/useWeekStart';
import currentWorklogActions from 'store/currentWorklog/actions';
import { isGeneralInfoChanged } from 'components/WorklogDetails/helpers';
import OptionalModal from 'components/OptionalModal/OptionalModal';
import CancelModal from 'components/WorklogDetails/components/CancelModal/CancelModal';
import { SearchSelect } from 'lib';
import DurationInput from 'components/DurationInput';
import {
  fetchLaborClassificationsForProject,
  selectLaborClassificationName,
} from 'store/laborClassifications';

const defaultModalState = { isOpen: false, text: '' };

const TM_HOURS = 'forceAccountTime.hours';
const TM_MINUTES = 'forceAccountTime.minutes';

const GeneralInfoForm = ({ closeDrawer }) => {
  const dispatch = useDispatch();
  const deletedAllLocations = useRef(null);
  const sheetFormClasses = useSheetFormStyles();
  const resourcesFormClasses = useResourcesFormStyles();
  const sheetDetailsClasses = useSheetDetailsStyles();

  const [wasInitialized, setWasInitialized] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [currContractorContacts, setCurrContractorContacts] = useState([]); //Fully Contacts
  const [projects, setProjects] = useState([]);
  const [projectLocations, setProjectLocations] = useState([]); //Fully location
  const [projectRallyPoints, setProjectRallyPoints] = useState([]); //Fully rally
  const [infoModal, setInfoModal] = useState(defaultModalState);
  const [overlappingModal, setOverlappingModal] = useState(null);
  const [sheetLocations, setSheetLocations] = useState(['']); //actual location _ids in selector
  const [sheetRallyPoints, setSheetRallyPoints] = useState(['']); //actual rally _ids in selector
  const [locationEditor, setLocationEditor] = useState(false);
  const [rallyPointEditor, setRallyPointEditor] = useState(false);
  const [showAddContactBtn, setShowAddContactBtn] = useState(true);
  const [showClearResources, setShowClearResources] = useState(false);
  const [showCancelAndMove, setShowCancelAndMove] = useState(false);
  const [showConfirmDateChange, setShowConfirmDateChange] = useState(false);

  const {
    handleSubmit,
    values,
    setValue,
    getValues,
    control,
    formState: { errors },
    register,
    unregister,
    trigger,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { weekDayFromO } = useWeekStart();
  const { dateFormat } = useDateSettingsFormat();
  const sheet = useSelector((store) => store.currentWorklog.worklogSheet.sheet);
  const allProjectsStored = useSelector((store) => store.projectTypes.projects.data);
  const allContractors = useSelector((store) => store.contractors.data);
  const defaultLcName = useSelector((store) =>
    selectLaborClassificationName(store, sheet?.defaultLaborClassificationId),
  );

  useEffect(() => {
    if (sheet?.projectId) {
      dispatch(fetchLaborClassificationsForProject(sheet.projectId)).catch(() => {});
    }
  }, [sheet?.projectId, dispatch]);
  const allProjects = useMemo(
    () => allProjectsStored.filter((proj) => proj.active),
    [allProjectsStored]
  );

  useEffect(() => {
    register({ name: 'projectId' });
    register({ name: 'contractorId' });
    register({ name: 'locations' });
    register({ name: 'rallyPoints' });
  }, [register]);

  const alternateContactsWatcher = watch('alternateContacts');
  useEffect(() => {
    handleShowAddContactBtn();
  }, [alternateContactsWatcher]);

  const valuesLocations = watch('locations');
  const valuesRallyPoints = watch('rallyPoints');

  
  const rallyPointOptions = useMemo(() => (
    projectRallyPoints.filter((location) => (
      !valuesRallyPoints?.find((loc) => location._id === loc)
    ))
  ), [projectRallyPoints, valuesRallyPoints])
  const locationOptions = useMemo(() => (
    projectLocations.filter((location) => (
      !valuesLocations?.find((loc) => location._id === loc)
    ))
  ), [projectLocations, valuesLocations])

  useEffect(() => {
    if (!sheet || !Object.keys(sheet).length || wasInitialized) return;
    const contractorId =
      (sheet.project && sheet.project.contractor && sheet.project.contractor.oldContractorId) ||
      sheet.contractorId;

    const projects = allProjects.filter((project) => project.contractorId === contractorId);

    const nextLocations =
      sheet.project?.locations && Array.isArray(sheet.project.locations)
        ? sheet.project.locations
        : [];
    const nextRallyPoints =
      sheet.project?.rallyPoints && Array.isArray(sheet.project.rallyPoints)
        ? sheet.project.rallyPoints
        : [];

    setContractors(allContractors);
    setProjects(projects);

    setProjectLocations(_.uniqBy([...nextLocations, ...(sheet?.sheetLocations || [])], '_id'));

    setProjectRallyPoints(
      _.uniqBy([...nextRallyPoints, ...(sheet?.sheetRallyPoints || [])], '_id')
    );

    setValue('contractorId', contractorId);
    setValue('forceAccount', sheet.forceAccount);
    setValue('createdAt', new Date(sheet.createdAt));
    // setValue('gridDate', sheet.grid.date);
    setValue(
      'gridDate',
      format(parse(sheet.grid.date, globalBEDateFormat, new Date()), dateFormat)
    );
    setValue('projectId', (sheet.project && sheet.project.oldProjectId) || sheet.projectId);

    const contractor = allContractors.find((contractor) => contractor._id === contractorId);

    const sheetAlternateContacts = [
      sheet?.primaryContact?._id || '',
      ...(sheet?.alternateContacts?.map((el) => el._id) || []),
    ];
    const sheetAllFullContacts = sheet?.primaryContact
      ? [sheet.primaryContact, ...(sheet?.alternateContacts || [])]
      : sheet?.alternateContacts || [];

    setValue(
      'alternateContacts',
      (sheetAlternateContacts?.length && sheetAlternateContacts) || ['']
    );

    setCurrContractorContacts(
      _.uniqBy(
        [...sheetAllFullContacts, ...(contractor?.contacts || []), ...(sheet?.contacts || [])],
        '_id'
      ) || [] // sheet?.contacts: if contact was saved into WL
    );

    if (sheet.locations && sheet.locations.length) {
      setSheetLocations(sheet.locations.slice());
    }
    if (sheet.rallyPoints && sheet.rallyPoints.length) {
      setSheetRallyPoints(sheet.rallyPoints.slice());
    }

    setWasInitialized(allProjectsStored?.length && allContractors?.length);
  }, [allContractors, allProjects, sheet]);

  useEffect(() => {
    setValue('locations', sheetLocations);
    setValue('rallyPoints', sheetRallyPoints);
  }, [sheetLocations, sheetRallyPoints]);

  const setDate = (value) => {
    setValue('createdAt', startOfDay(parse(value, dateFormat, new Date())));
    setValue('gridDate', value);
  };

  const setGridDate = (value) => {
    // const [day, month, year] = value.split('/');
    setValue('gridDate', value);
  };

  const formatLocationName = (location) => {
    const a = location.address;
    const b = location.zipcode ? `, ${location.zipcode}` : '';
    const c = location.name ? ` - ${location.name}` : '';
    return `${a}${b}${c}`;
  };

  const makeData = (values, cancelNote) => {
    const data = values;
    data.createdAt = new Date(data.createdAt).toISOString();
    data.locations = data?.locations?.filter((loc) => !!loc);
    data.rallyPoints = data?.rallyPoints?.filter((loc) => !!loc);
    data.addLocations = [
      ...projectLocations.filter((el) => el.new),
      ...projectRallyPoints.filter((el) => el.new),
    ];
    data.primaryContact = data?.alternateContacts.filter((loc) => !!loc)[0] || '';
    data.alternateContacts = data?.alternateContacts?.filter((loc) => !!loc)?.slice(1) || [];
    data.addContacts = currContractorContacts.filter((el) => el.new);
    data.gridDate = format(parse(data.gridDate, dateFormat, new Date()), globalBEDateFormat);

    if (cancelNote) {
      data.cancelNote = cancelNote;
    }

    return data;
  }

  const onSubmit = (skipValidation) => async (values) => {
    if (errors && Object.keys(errors).length) return;

    const data = makeData(values);

    if (
      new Date(sheet?.createdAt)?.toISOString() !== new Date(data?.createdAt)?.toISOString()
    ) {
      if (
        !skipValidation &&
        !sheet.canceledAt
      ) {
        if (sheet.workers?.length || sheet.equipment?.length) {
          return setShowClearResources(true);
        }
        if (sheet.published) return setShowConfirmDateChange(true);
      } else {
        delete data?.createdAt;
        data.gridDate = sheet.grid?.date;
      }
    }

    try {
      const updateGeneralInfo = () => {
        dispatch(patchWorklogGeneralInfo(data, sheet._id));
        dispatch(updateSheetAddResource(sheet._id, 1));
        closeDrawer();
      }

      if (!isGeneralInfoChanged(sheet, data)) {
        return closeDrawer();
      }

      updateGeneralInfo();

      if (sheet.submittedAt) {
        dispatch(currentWorklogActions.submittedChangesModalWlChanged());
      }
    } catch (e) {
      setInfoModal({ isOpen: true, text: e.message });
      console.log(e);
    }
  };

  const onSubmitCancelAndMove = useCallback((cancelNote) => {
    if (errors && Object.keys(errors).length) return;
    if (sheet.submittedAt || sheet.canceledAt) {
      return;
    }

    const values = getValues();
    const data = makeData(values, cancelNote);

    try {
      const cancelAndMove = () => {
        dispatch(postWorklogCancelAndMove(data, sheet._id));
        dispatch(updateSheetAddResource(sheet._id, 1));
        closeDrawer();
      }

      if (!isGeneralInfoChanged(sheet, data)) {
        return closeDrawer();
      }

      cancelAndMove();
    } catch (e) {
      setInfoModal({ isOpen: true, text: e.message });
      console.log(e);
    }
  }, [getValues]);


  const handleSelectContractor = (contractorId) => {
    const id = contractorId ?? '';

    setValue('contractorId', id);
    const contractorProjects = allProjects.filter((project) => project.contractorId === id);

    const contractor = contractors.find((contractor) => {
      return contractor._id === id;
    });

    setValue('alternateContacts', ['']);
    setCurrContractorContacts([...(contractor?.contacts || []), ...(sheet?.contacts || [])]);

    setProjects(contractorProjects);
    setValue('projectId', '');
    setError('projectId', '');
    setProjectLocations([]);
    setProjectRallyPoints([]);
    setSheetLocations(['']);
    setSheetRallyPoints(['']);
  };

  const handleSelectProject = (projectId) => {
    const id = projectId ?? '';

    setValue('projectId', id);
    const errorAction = id ? clearErrors : setError;
    errorAction('projectId');

    const project = projects.find((project) => project._id === id);
    const nextLocations =
      project && project.locations && Array.isArray(project.locations) ? project.locations : [];
    const nextRallyPoints =
      project && project.rallyPoints && Array.isArray(project.rallyPoints)
        ? project.rallyPoints
        : [];
    setProjectLocations(_.uniqBy([...nextLocations, ...(sheet?.sheetLocations || [])], '_id'));
    setProjectRallyPoints(
      _.uniqBy([...nextRallyPoints, ...(sheet?.sheetRallyPoints || [])], '_id')
    );

    setSheetLocations(['']);
    setSheetRallyPoints(['']);
  };

  const handleAddContact = () => {
    if (currContractorContacts.length > getValues().alternateContacts.length) {
      setValue('alternateContacts', [...getValues().alternateContacts, '']);
    }
  };

  const handleShowAddContactBtn = () => {
    if (currContractorContacts.length > getValues().alternateContacts.length) {
      setShowAddContactBtn(true);
    } else {
      setShowAddContactBtn(false);
    }
  };

  const handleSelectLocation = (index) => (locationId) => {
    const id = locationId ?? '';

    setSheetLocations((prevSheetLocations) => {
      prevSheetLocations[index] = id;
      return prevSheetLocations.slice();
    });
  };
  const handleSelectRallyPoint = (index) => (rallyPointId) => {
    const id = rallyPointId ?? '';

    setSheetRallyPoints((prevSheetRallyPoint) => {
      prevSheetRallyPoint[index] = id;
      return prevSheetRallyPoint.slice();
    });
  };

  const handleLocationEditor = (value) => {
    if (value === false) {
      setLocationEditor(value);
      return;
    }
    setLocationEditor(value.toString());
  };

  const handleRallyPointEditor = (value) => {
    if (value === false) {
      setRallyPointEditor(value);
      return;
    }
    setRallyPointEditor(value.toString());
  };

  const handleRemoveLocation = (index) => () => {
    const isItAlreadyDeletedFromProj = deletedAllLocations.current?.deletedLocations?.some(
      (el) => el._id === sheetLocations[index]
    );
    if (isItAlreadyDeletedFromProj) {
      setProjectLocations((prev) => prev.filter((el) => el._id != sheetLocations[index]));
    }
    if (sheetLocations.length === 1) {
      setSheetLocations(['']);
    } else {
      setSheetLocations((prevSheetLocations) => {
        prevSheetLocations.splice(index, 1);
        return prevSheetLocations.slice();
      });
    }
  };
  const handleRemoveRallyPoints = (index) => () => {
    const isItAlreadyDeletedFromProj = deletedAllLocations.current?.deletedRallyPoints?.some(
      (el) => el._id === sheetRallyPoints[index]
    );
    if (isItAlreadyDeletedFromProj) {
      setProjectRallyPoints((prev) => prev.filter((el) => el._id != sheetRallyPoints[index]));
    }
    if (sheetRallyPoints.length === 1) {
      setSheetRallyPoints(['']);
    } else {
      setSheetRallyPoints((prevRallyPoints) => {
        prevRallyPoints.splice(index, 1);
        return prevRallyPoints.slice();
      });
    }
  };

  const handleAppendLocation = () => {
    if (projectLocations.length > sheetLocations.length) {
      setSheetLocations((prevSheetLocations) => [...prevSheetLocations, '']);
    }
  };
  const handleAppendRallyPoints = () => {
    if (projectRallyPoints.length > sheetRallyPoints.length) {
      setSheetRallyPoints((prevSheetRallyPoints) => [...prevSheetRallyPoints, '']);
    }
  };

  const closeModal = () => setInfoModal(defaultModalState);
  const closeOverlappingModal = () => setOverlappingModal(null);

  const forceAccount = watch('forceAccount');

  const validateTmHours = () => {
    const values = getValues();
    if (!/^[0-9]+$/i.test(values.forceAccountTime && values.forceAccountTime.hours)) {
      return 'The number is invalid. The field can contain figures only';
    } else if (
      (+values.forceAccountTime && +values.forceAccountTime.hours > 48) ||
      (+values.forceAccountTime && +values.forceAccountTime.hours < 0)
    ) {
      return 'Must be a number from 0 to 48';
    }
    return true;
  };

  useEffect(() => {
    if (forceAccount !== 'Partial') return undefined;
    register({ name: TM_HOURS }, { validate: validateTmHours });
    register({ name: TM_MINUTES });
    setValue(TM_HOURS, sheet.forceAccountTime?.hours || '0');
    setValue(TM_MINUTES, sheet.forceAccountTime?.minutes || '00');
    return () => unregister([TM_HOURS, TM_MINUTES]);
  }, [forceAccount]);

  const setTmTime = (time) => {
    setValue(TM_HOURS, String(time.hours));
    setValue(TM_MINUTES, String(time.minutes).padStart(2, '0'));
    trigger(TM_HOURS);
  };

  const getProjectDocs = () => {
    return [
      ...(allProjects?.find((proj) => proj._id === getValues()?.projectId)?.documentStorage || []),
    ].sort((a, b) => a?.name?.localeCompare(b?.name));
  };

  const now = new Date();
  const isFieldTechnician = checkUserRole('Field Technician');
  const calendarStartOfWeek = isFieldTechnician
    ? format(subDays(startOfWeek(now, { weekStartsOn: weekDayFromO }), 1), dateFormat)
    : null;
  const calendarEndOfWeek = isFieldTechnician
    ? format(addDays(endOfWeek(now, { weekStartsOn: weekDayFromO }), 1), dateFormat)
    : null;

  // const shouldDisplayDateTooltip = (sheet.workers.length || sheet.equipment.length) && !sheet.submittedAt;

  const getConfirmDateChangeText = () => {
    const values = getValues();
    if (!sheet?.createdAt || !values?.createdAt) return '';
    const prevDate = format(new Date(sheet.createdAt), dateFormat);
    const nextDate = format(new Date(values.createdAt), dateFormat);
    return `This WL has been published. Would you like to Cancel for ${prevDate} and move it to ${nextDate}?`
  };

  const isDateChangeAllowed = (
    (!sheet.submittedAt && !sheet.canceledAt) &&
    (hasPermissionsFor('scheduleEdit') || hasPermissionsFor('scheduleFullAccess'))
  );

  return (
    <div className={resourcesFormClasses.formWrapper} role="presentation">
      {!Object.keys(sheet).length ? (
        <PageLoader loading>
          <div />
        </PageLoader>
      ) : (
        <PageLoader loading={false} small>
          <form onSubmit={handleSubmit(onSubmit())}>
            <div className={resourcesFormClasses.formFlex}>
              <div>
                <Typography variant="h3" style={{ marginBottom: '26px' }}>
                  Edit sheet
                </Typography>

                <IconButton
                  className={resourcesFormClasses.closeButton}
                  onClick={closeDrawer}
                  aria-label="Close"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>

                <Grid container spacing={4}>
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                    <label htmlFor="contractorId">
                      <Typography variant="body1" color="textSecondary">
                        Contractor:
                      </Typography>
                      <SearchSelect
                        value={watch('contractorId')}
                        onChange={handleSelectContractor}
                        options={contractors.slice().sort((a, b) => a.name.localeCompare(b.name))}
                        error={Boolean(errors.contractorId)}
                        inputProps={{ id: 'contractorId', 'aria-label': 'Contractor' }}
                      />
                      {errors.contractorId ? (
                        <div className={sheetDetailsClasses.error}>{errors.contractorId}</div>
                      ) : null}
                    </label>
                  </Grid>
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                    <label htmlFor="forceAccount">
                      <Typography variant="body1" color="textSecondary">
                        Time & Material:
                      </Typography>
                      <Controller
                        name="forceAccount"
                        control={control}
                        defaultValue=""
                        as={
                          <TextField
                            select
                            id="forceAccount"
                            className={sheetDetailsClasses.textField}
                            variant="outlined"
                            inputProps={{ 'aria-label': 'Time & Material' }}
                            SelectProps={{
                              native: isIOS,
                            }}
                            fullWidth
                          >
                            <Option key={0} value="No">
                              No
                            </Option>
                            <Option key={1} value="Full">
                              Full
                            </Option>
                            <Option key={2} value="Partial">
                              Partial
                            </Option>
                          </TextField>
                        }
                      />
                    </label>
                  </Grid>
                  <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                    <label htmlFor="createdAt">
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        style={{ display: 'flex', alignItems: 'end' }}
                      >
                        Date:
                        {/* {shouldDisplayDateTooltip ? (
                          <Tooltip
                            title={'If you would like to change the date, please clear the resources first'}
                            placement="top"
                            enterTouchDelay={0}
                          >
                            <InfoIcon
                              fontSize="small"
                              style={{ width: 16, height: 16, marginLeft: 5, display: 'flex' }}
                              color="primary"
                            />
                          </Tooltip>
                        ) : null} */}
                      </Typography>
                      <Controller
                        name="createdAt"
                        control={control}
                        defaultValue=""
                        as={({ value }) => {
                          return (
                            <DatePickerInput
                              selectedDay={value}
                              setFieldValue={(_, value) => setDate(value)}
                              field="createdAt"
                              fullWidth
                              sm
                              disabled={!isDateChangeAllowed}
                              minDate={calendarStartOfWeek}
                              maxDate={calendarEndOfWeek}
                            />
                          );
                        }}
                      />
                    </label>
                  </Grid>
                  <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                    <label htmlFor="gridDate">
                      <Typography variant="body1" color="textSecondary">
                        Grid view date:
                      </Typography>
                      <Controller
                        name="gridDate"
                        control={control}
                        defaultValue=""
                        as={({ value }) => {
                          return (
                            <DatePickerInput
                              selectedDay={value}
                              setFieldValue={(_, value) => setGridDate(value)}
                              field="gridDate"
                              alignRight
                              fullWidth
                              sm
                              minDate={calendarStartOfWeek}
                              maxDate={calendarEndOfWeek}
                            />
                          );
                        }}
                      />
                    </label>
                  </Grid>
                  {forceAccount === 'Partial' && (
                    <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                      <Typography variant="body1" color="textSecondary">
                        T&M Time:
                      </Typography>
                      <div className={sheetDetailsClasses.pickers}>
                        <DurationInput
                          title="T&M time"
                          value={watch('forceAccountTime') || {}}
                          onChange={setTmTime}
                          error={Boolean(errors.forceAccountTime)}
                          maxHours={48}
                          minuteStep={5}
                        />
                      </div>
                      {errors.forceAccountTime && errors.forceAccountTime.hours && (
                        <div className={sheetDetailsClasses.error}>
                          {errors.forceAccountTime.hours.message}
                        </div>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                    <label htmlFor="projectId">
                      <Typography variant="body1" color="textSecondary">
                        Project:
                      </Typography>
                      <SearchSelect
                        value={watch('projectId')}
                        onChange={handleSelectProject}
                        options={projects.slice().sort((a, b) => a.route.localeCompare(b.route))}
                        getOptionLabel={(option) => getProjectName(option, { jobNumber: true })}
                        error={Boolean(errors.projectId)}
                        inputProps={{ id: 'projectId', 'aria-label': 'Project' }}
                      />
                      {errors.projectId && (
                        <div className={sheetDetailsClasses.error}>This field can't be blank</div>
                      )}
                    </label>
                  </Grid>
                  {sheet?.defaultLaborClassificationId && defaultLcName && (
                    <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                      <Typography variant="body1" color="textSecondary" component="span">
                        Default Labor Classification:&nbsp;
                      </Typography>
                      <Typography variant="body1" component="span">
                        {defaultLcName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                    <Divider />
                  </Grid>
                  {!!getProjectDocs()?.length && (
                    <>
                      <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                        <Typography variant="body1" color="textSecondary">
                          Documents:
                        </Typography>
                        <Grid
                          item
                          xs={12}
                          lg={12}
                          className={classnames(sheetDetailsClasses.zeroPaddingTop)}
                        >
                          {getProjectDocs()?.length === 1 ? (
                            <span>
                              <Link
                                variant="body1"
                                className={sheetDetailsClasses.docLinks}
                                href={getProjectDocs()[0].url}
                                target="_blank"
                              >
                                {getProjectDocs()[0].name}
                              </Link>
                              &nbsp;
                            </span>
                          ) : (
                            <SearchSelect
                              value={null}
                              onChange={(id) => {
                                const doc = getProjectDocs().find((option) => option._id === id);
                                if (doc) window.open(doc.url, '_black', 'noreferrer');
                              }}
                              options={getProjectDocs()}
                              error={Boolean(errors.contractorId)}
                              inputProps={{ id: 'documentStorageId', 'aria-label': 'Document' }}
                              renderOption={(option) => (
                                <MenuItem
                                  key={option.name}
                                  value={option.name}
                                >
                                  {option.name}
                                </MenuItem>
                              )}
                            />
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                        <Divider />
                      </Grid>
                    </>
                  )}
                  <AlternateContacts
                    getValues={getValues}
                    setValue={setValue}
                    sheetDetailsClasses={sheetDetailsClasses}
                    setCurrContractorContacts={setCurrContractorContacts}
                    currContractorContacts={currContractorContacts}
                    control={control}
                    CloseIcon={CloseIcon}
                  />

                  {showAddContactBtn && (
                    <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                      <div className={sheetDetailsClasses.primaryAdd} onClick={handleAddContact}>
                        <span>+ Add Alternate Contact</span>
                      </div>
                    </Grid>
                  )}
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                    <Divider />
                  </Grid>
                  {sheetRallyPoints &&
                    sheetRallyPoints.map((rallyPointId, index) => (
                      <Grid
                        item
                        xs={12}
                        className={sheetDetailsClasses.zeroPaddingTop}
                        key={rallyPointId + index}
                      >
                        <label htmlFor={`rallyPoints_${index}`}>
                          <Typography variant="body1" color="textSecondary">
                            Rally Point{sheetRallyPoints.length === 1 ? null : ` ${index + 1}`}:
                          </Typography>
                          <div className={classnames(sheetDetailsClasses.flexBox, sheetDetailsClasses.fullWidth)}>
                            <div className={sheetDetailsClasses.flexGrow}>
                              <SearchSelect
                                value={rallyPointId}
                                onChange={handleSelectRallyPoint(index)}
                                options={[
                                  projectRallyPoints.find((pLoc) => pLoc._id === rallyPointId),
                                  ...rallyPointOptions,
                                ].filter(Boolean)}
                                getOptionLabel={(option) => formatLocationName(option)}
                                error={Boolean(errors.rallyPoints?.[index])}
                                inputProps={{ id: `rallyPoints[${index}]`, 'aria-label': 'Rally Point' }}
                                footer={
                                  getValues()?.projectId && (
                                    <Button
                                      variant="text"
                                      className={sheetDetailsClasses.primaryTextButton}
                                      onClick={() => handleRallyPointEditor(index)}
                                      disableRipple
                                    >
                                      + Create New Rally Point
                                    </Button>
                                  )
                                }
                              />
                            </div>
                            <IconButton
                              className={sheetDetailsClasses.deleteLocationButton}
                              onClick={handleRemoveRallyPoints(index)}
                              aria-label="Delete"
                              disableTouchRipple
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </label>
                      </Grid>
                    ))}
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPadding}>
                    {rallyPointEditor ? (
                      <LocationCreator
                        locationEditor={rallyPointEditor}
                        values={getValues()}
                        handleLocationEditor={handleRallyPointEditor}
                        setSheetRallyPoints={setSheetRallyPoints}
                        setFieldValue={setValue}
                        setLocations={setProjectRallyPoints}
                        type={'rallyPoint'}
                        fieldName={'rallyPoints'}
                        sheet={sheet}
                      />
                    ) : null}
                  </Grid>
                  {sheetRallyPoints &&
                    projectRallyPoints.length > sheetRallyPoints.length &&
                    !rallyPointEditor && (
                      <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                        <Button
                          variant="text"
                          className={sheetDetailsClasses.primaryTextButton}
                          onClick={handleAppendRallyPoints}
                          disableRipple
                        >
                          + Add Rally Point
                        </Button>
                      </Grid>
                    )}
                  <Grid container spacing={4} className={sheetDetailsClasses.paddingBottomBig}>
                    <Grid item xs={11} style={{ marginLeft: '15px' }}>
                      <Divider />
                    </Grid>
                  </Grid>
                  {sheetLocations &&
                    sheetLocations.map((locationId, index) => (
                      <Grid
                        item
                        xs={12}
                        className={sheetDetailsClasses.zeroPaddingTop}
                        key={locationId + index}
                      >
                        <label htmlFor={`locations_${index}`}>
                          <Typography variant="body1" color="textSecondary">
                            Project Location{sheetLocations.length === 1 ? null : ` ${index + 1}`}:
                          </Typography>
                          <div className={classnames(sheetDetailsClasses.flexBox, sheetDetailsClasses.fullWidth)}>
                            <div className={sheetDetailsClasses.flexGrow}>
                              <SearchSelect
                                value={locationId}
                                onChange={handleSelectLocation(index)}
                                options={[
                                  projectLocations.find((pLoc) => pLoc._id === locationId),
                                  ...locationOptions,
                                ].filter(Boolean)}
                                getOptionLabel={(option) => formatLocationName(option)}
                                error={Boolean(errors.locations?.[index])}
                                inputProps={{ id: `locations[${index}]`, 'aria-label': 'Project Location' }}
                                footer={
                                  getValues()?.projectId && (
                                    <Button
                                      variant="text"
                                      className={sheetDetailsClasses.primaryTextButton}
                                      onClick={() => handleLocationEditor(index)}
                                      disableRipple
                                    >
                                      + Create New Project Location
                                    </Button>
                                  )
                                }
                              />
                            </div>
                            <IconButton
                              className={sheetDetailsClasses.deleteLocationButton}
                              onClick={handleRemoveLocation(index)}
                              aria-label="Delete"
                              disableTouchRipple
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </label>
                      </Grid>
                    ))}
                  <Grid item xs={12} className={sheetDetailsClasses.zeroPadding}>
                    {locationEditor ? (
                      <LocationCreator
                        locationEditor={locationEditor}
                        values={getValues()}
                        handleLocationEditor={handleLocationEditor}
                        setSheetRallyPoints={setSheetLocations}
                        setFieldValue={setValue}
                        setLocations={setProjectLocations}
                        type={'projectLocation'}
                        fieldName={'locations'}
                        sheet={sheet}
                      />
                    ) : null}
                  </Grid>
                  {sheetLocations &&
                    projectLocations.length > sheetLocations.length &&
                    !locationEditor && (
                      <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                        <Button
                          variant="text"
                          className={sheetDetailsClasses.primaryTextButton}
                          onClick={handleAppendLocation}
                          disableRipple
                        >
                          + Add Project Location
                        </Button>
                      </Grid>
                    )}
                </Grid>

                <div className={sheetFormClasses.buttonHolder}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    className={sheetFormClasses.cancelButton}
                    onClick={closeDrawer}
                    disableTouchRipple
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="outlined"
                    className={sheetFormClasses.saveButton}
                    disableTouchRipple
                    disabled={errors && !!Object.keys(errors).length}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </PageLoader>
      )}
      {infoModal.isOpen && (
        <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeModal} />
      )}
      {overlappingModal && (
        <OverlappingModal
          data={overlappingModal.data}
          message={overlappingModal.reason}
          onClose={closeOverlappingModal}
        />
      )}
      {showClearResources && (
        <OptionalModal
          isOpen={showClearResources}
          onSave={() => {
            setShowClearResources(false);
            if (sheet.published) {
              setShowConfirmDateChange(true);
            } else {
              onSubmitCancelAndMove('')
            }
          }}
          onCancel={handleSubmit(onSubmit(true))}
          onClose={() => setShowClearResources(false)}
          options={{
            title: 'Clear Assigned Resources',
            text: 'Before you can change the date for this WL, the assigned resources must be cleared. Do you want to clear the resources?',
            cancelBtnText: 'No',
            saveBtnText: 'Yes',
          }}
        />
      )}
      {showConfirmDateChange && (
        <OptionalModal
          isOpen={showConfirmDateChange}
          onSave={() => {
            setShowClearResources(false);
            setShowConfirmDateChange(false);
            setShowCancelAndMove(true);
          }}
          onCancel={handleSubmit(onSubmit(true))}
          onClose={() => {
            setShowConfirmDateChange(false);
          }}
          options={{
            title: 'Confirm WL Date Change',
            text: getConfirmDateChangeText(),
            cancelBtnText: 'No',
            saveBtnText: 'Yes',
          }}
        />
      )}
      {showCancelAndMove && (
        <CancelModal
          isOpen={showCancelAndMove}
          onSubmit={onSubmitCancelAndMove}
          onClose={() => {
            setShowCancelAndMove(false);
            setShowConfirmDateChange(true);
          }}
          hideCloseButton={true}
          buttonText="Mark and Duplicate"
        /> 
      )}
    </div>
  );
};

GeneralInfoForm.propTypes = {
  closeDrawer: propTypes.func.isRequired,
  sheet: propTypes.object.isRequired,
};

export default GeneralInfoForm;
